/**
 * ECharts Lazy Loader
 *
 * Tree-shakeable imports from echarts/core — only registers the chart types
 * and components used by the cash flow charts. Keeps ECharts out of the main
 * bundle (~150-200KB gzipped) until a chart is actually rendered.
 *
 * Also registers a 'sweetspot-dark' theme derived from the Tailwind zinc palette.
 */
import type {
  BarSeriesOption,
  LineSeriesOption,
  ScatterSeriesOption,
} from 'echarts/charts'
import type {
  AriaComponentOption,
  DataZoomComponentOption,
  GridComponentOption,
  LegendComponentOption,
  MarkAreaComponentOption,
  MarkLineComponentOption,
  TooltipComponentOption,
} from 'echarts/components'
import type { ComposeOption, ECharts } from 'echarts/core'

export type EChartsOption = ComposeOption<
  | BarSeriesOption
  | LineSeriesOption
  | ScatterSeriesOption
  | GridComponentOption
  | TooltipComponentOption
  | LegendComponentOption
  | DataZoomComponentOption
  | MarkLineComponentOption
  | MarkAreaComponentOption
  | AriaComponentOption
>

// Singleton promise: ECharts is loaded and registered exactly once.
async function loadECharts() {
  const [
    { use: registerComponents, init, registerTheme },
    { BarChart, LineChart, ScatterChart },
    {
      GridComponent,
      TooltipComponent,
      LegendComponent,
      DataZoomComponent,
      MarkLineComponent,
      MarkAreaComponent,
      AriaComponent,
    },
    { CanvasRenderer },
  ] = await Promise.all([
    import('echarts/core'),
    import('echarts/charts'),
    import('echarts/components'),
    import('echarts/renderers'),
  ])

  registerComponents([
    BarChart,
    LineChart,
    ScatterChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
    DataZoomComponent,
    MarkLineComponent,
    MarkAreaComponent,
    AriaComponent,
    CanvasRenderer,
  ])

  // Dark theme: zinc-based palette matching the Tailwind config
  registerTheme('sweetspot-dark', {
    backgroundColor: 'transparent',
    textStyle: { color: '#a1a1aa' }, // zinc-400
    title: { textStyle: { color: '#e4e4e7' } }, // zinc-200
    legend: {
      textStyle: { color: '#a1a1aa' }, // zinc-400
    },
    tooltip: {
      backgroundColor: '#18181b', // zinc-900
      borderColor: '#3f3f46', // zinc-700
      textStyle: { color: '#e4e4e7' }, // zinc-200
    },
    categoryAxis: {
      axisLine: { lineStyle: { color: '#3f3f46' } }, // zinc-700
      axisTick: { lineStyle: { color: '#3f3f46' } },
      axisLabel: { color: '#a1a1aa' }, // zinc-400
      splitLine: { lineStyle: { color: 'rgba(63, 63, 70, 0.4)' } },
    },
    valueAxis: {
      axisLine: { lineStyle: { color: '#3f3f46' } },
      axisTick: { lineStyle: { color: '#3f3f46' } },
      axisLabel: { color: '#a1a1aa' },
      splitLine: { lineStyle: { color: 'rgba(63, 63, 70, 0.4)' } },
    },
    timeAxis: {
      axisLine: { lineStyle: { color: '#3f3f46' } },
      axisTick: { lineStyle: { color: '#3f3f46' } },
      axisLabel: { color: '#a1a1aa' },
      splitLine: { lineStyle: { color: 'rgba(63, 63, 70, 0.4)' } },
    },
    dataZoom: {
      borderColor: '#3f3f46',
      textStyle: { color: 'transparent' },
      handleStyle: {
        color: '#71717a', // zinc-500
        borderColor: '#71717a',
      },
      fillerColor: 'rgba(113, 113, 122, 0.15)',
      dataBackground: {
        lineStyle: { color: 'rgba(161, 161, 170, 0.3)' }, // zinc-400 @ 30%
        areaStyle: { color: 'rgba(161, 161, 170, 0.08)' },
      },
      selectedDataBackground: {
        lineStyle: { color: 'rgba(96, 165, 250, 0.5)' }, // blue-400 @ 50%
        areaStyle: { color: 'rgba(96, 165, 250, 0.12)' },
      },
    },
  })

  return { init }
}

let echartsPromise: ReturnType<typeof loadECharts> | null = null

/**
 * Lazily load ECharts with required chart types and components registered.
 * Returns `{ init }` — the ECharts init function. Cached after first call.
 */
export function getECharts(): Promise<{
  init: typeof import('echarts/core').init
}> {
  if (!echartsPromise) {
    echartsPromise = loadECharts()
  }
  return echartsPromise
}

export type { ECharts }
